import { useMemo, useState } from "react";
import { ScanLine, GraduationCap, Home, Check, X, RotateCcw } from "lucide-react";

const ACCENT = "#0d9488";

/* ── Periodontal pocket cross-section that draws to the probed depth ───────── */
export function PocketDiagram({ pocket, gi }) {
  const mm = pocket ?? 0;
  const scale = 7; // px per mm
  const gumTop = 52;
  const tipY = gumTop + Math.min(mm, 11) * scale;
  const tone = mm >= 6 ? "#ef4444" : mm >= 4 ? "#f59e0b" : "#10b981";
  const gum = gi >= 2 ? "#e11d48" : gi === 1 ? "#fb7185" : "#fda4af";
  const ticks = Array.from({ length: 12 }, (_, i) => gumTop + i * scale);

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="pocket-diagram">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Pocket cross-section</span>
        <span className="text-[11px] font-bold font-mono" style={{ color: tone }} data-testid="pocket-diagram-depth">
          {pocket == null ? "—" : `${mm} mm`} · {mm >= 6 ? "deep" : mm >= 4 ? "moderate" : "healthy"}
        </span>
      </div>
      <svg viewBox="0 0 220 150" className="w-full" style={{ maxHeight: 150 }}>
        {/* bone */}
        <rect x="0" y={gumTop + 8} width="220" height="120" fill="#fde68a" opacity="0.45" />
        {/* tooth */}
        <path d="M92 12 Q110 6 128 12 L126 130 Q110 140 94 130 Z" fill="#fff" stroke="#cbd5e1" strokeWidth="2" />
        <path d="M92 12 Q110 6 128 12 L127 40 Q110 46 93 40 Z" fill="#f8fafc" />
        {/* gingiva left + right */}
        <path d={`M0 ${gumTop} Q60 ${gumTop - 6} 88 ${gumTop + 6} L88 150 L0 150 Z`} fill={gum} stroke="#fb7185" strokeWidth="1" />
        <path d={`M220 ${gumTop} Q160 ${gumTop - 6} 132 ${gumTop + 6} L132 150 L220 150 Z`} fill={gum} stroke="#fb7185" strokeWidth="1" />
        {/* pocket highlight (left sulcus) */}
        <rect x="84" y={gumTop} width="8" height={Math.min(mm, 11) * scale} fill={tone} opacity="0.5" />
        {/* periodontal probe with mm bands */}
        <g>
          <rect x="78" y="6" width="5" height={tipY - 6} rx="2" fill="#64748b" />
          {ticks.filter((y) => y <= tipY).map((y, i) => (
            <rect key={i} x="78" y={y} width="5" height={scale / 2} fill={i % 2 ? "#1e293b" : "#e2e8f0"} />
          ))}
          <circle cx="80.5" cy={tipY} r="3" fill={tone} />
        </g>
        {/* healthy attachment reference (3mm) */}
        <line x1="60" y1={gumTop + 3 * scale} x2="84" y2={gumTop + 3 * scale} stroke="#10b981" strokeWidth="1.5" strokeDasharray="3 2" />
      </svg>
      <p className="text-[10px] text-slate-400 leading-snug">Probe depth drawn to scale; dashed line = healthy ≤3 mm sulcus.</p>
    </div>
  );
}

/* ── Before / after dental radiograph toggle ───────────────────────────────── */
export function RadiographToggle() {
  const [after, setAfter] = useState(false);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="radiograph-card">
      <div className="flex items-center justify-between mb-3">
        <h3 className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          <ScanLine className="w-3.5 h-3.5" /> Dental radiograph
        </h3>
        <div className="flex rounded-md overflow-hidden border border-slate-200 text-[11px] font-semibold">
          <button type="button" data-testid="xray-before-btn" onClick={() => setAfter(false)} className={`px-2.5 py-1 ${!after ? "bg-slate-800 text-white" : "bg-white text-slate-500"}`}>Before</button>
          <button type="button" data-testid="xray-after-btn" onClick={() => setAfter(true)} className={`px-2.5 py-1 ${after ? "text-white" : "bg-white text-slate-500"}`} style={after ? { background: ACCENT } : undefined}>After</button>
        </div>
      </div>
      <div className="relative rounded-lg overflow-hidden border-2 border-slate-700 bg-black" style={{ aspectRatio: "3 / 2" }} data-testid="xray-image">
        <svg viewBox="0 0 240 160" className="w-full h-full">
          <rect width="240" height="160" fill="#0b0b0b" />
          {/* alveolar bone level */}
          <path d={after ? "M10 96 Q120 92 230 96" : "M10 96 Q70 122 120 110 Q170 100 230 96"} fill="#3a3a3a" opacity="0.85" />
          {/* three teeth (crown + roots) */}
          {[60, 120, 180].map((cx, i) => (
            <g key={cx}>
              <rect x={cx - 16} y="40" width="32" height="34" rx="5" fill="#dfe6ee" opacity="0.9" />
              <path d={`M${cx - 12} 74 L${cx - 7} 128 L${cx - 2} 74 Z`} fill="#cdd6df" opacity="0.85" />
              <path d={`M${cx + 12} 74 L${cx + 7} 128 L${cx + 2} 74 Z`} fill="#cdd6df" opacity="0.85" />
              {/* periapical lucency (abscess) on the middle tooth — before only */}
              {!after && i === 1 && <circle cx={cx} cy="128" r="11" fill="#050505" stroke="#222" strokeWidth="1" />}
              {/* resorptive lesion notch — before only, side tooth */}
              {!after && i === 2 && <rect x={cx - 4} y="64" width="8" height="8" fill="#050505" />}
            </g>
          ))}
        </svg>
        <span className="absolute bottom-1.5 left-1.5 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded" style={{ background: after ? ACCENT : "#ef4444", color: "#fff" }} data-testid="xray-label">
          {after ? "POST-TREATMENT" : "PRE-TREATMENT"}
        </span>
      </div>
      <p className="text-[11px] text-slate-500 mt-2 leading-snug">
        {after
          ? "After extraction/treatment: bone margin restored, the periapical lucency and resorptive lesion resolved."
          : "Pre-treatment: horizontal bone loss, a periapical lucency (abscess) and a resorptive lesion — radiographs reveal disease hidden below the gumline."}
      </p>
    </div>
  );
}

/* ── AVDC abbreviations quiz (built from the symbol palette) ────────────────── */
export function AvdcQuiz({ symbols }) {
  const pool = useMemo(() => symbols.filter((s) => s.name).slice(0, 8), [symbols]);
  const [order] = useState(() => [...pool].sort(() => Math.random() - 0.5).slice(0, Math.min(5, pool.length)));
  const [i, setI] = useState(0);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  const q = order[i];
  const choices = useMemo(() => {
    if (!q) return [];
    const wrong = pool.filter((s) => s.code !== q.code).sort(() => Math.random() - 0.5).slice(0, 2);
    return [q, ...wrong].sort(() => Math.random() - 0.5);
  }, [q, pool]);

  if (!q) return null;

  const choose = (code) => {
    if (picked) return;
    setPicked(code);
    if (code === q.code) setScore((s) => s + 1);
  };
  const next = () => {
    if (i + 1 >= order.length) setDone(true);
    else { setI(i + 1); setPicked(null); }
  };
  const restart = () => { setI(0); setPicked(null); setScore(0); setDone(false); };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="avdc-quiz">
      <h3 className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
        <GraduationCap className="w-3.5 h-3.5" /> AVDC abbreviations quiz
      </h3>
      {done ? (
        <div className="text-center py-3" data-testid="avdc-quiz-result">
          <div className="text-2xl font-bold" style={{ color: ACCENT }}>{score}/{order.length}</div>
          <div className="text-xs text-slate-500 mb-3">AVDC abbreviations correct</div>
          <button type="button" onClick={restart} data-testid="avdc-quiz-restart" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#0f766e] hover:underline">
            <RotateCcw className="w-3.5 h-3.5" /> Try again
          </button>
        </div>
      ) : (
        <>
          <div className="text-sm text-slate-600 mb-2">What does <span className="font-mono font-bold text-slate-900 text-base">{q.code}</span> stand for? <span className="text-slate-400">({i + 1}/{order.length})</span></div>
          <div className="space-y-2">
            {choices.map((o) => {
              let cls = "border-slate-200 hover:border-slate-300 text-slate-700";
              if (picked) {
                if (o.code === q.code) cls = "border-emerald-400 bg-emerald-50 text-emerald-700";
                else if (o.code === picked) cls = "border-rose-400 bg-rose-50 text-rose-600";
                else cls = "border-slate-200 text-slate-400 opacity-70";
              }
              return (
                <button key={o.code} type="button" disabled={!!picked} onClick={() => choose(o.code)} data-testid={`avdc-choice-${o.code}`} className={`w-full text-left text-sm font-medium border-2 rounded-md px-3 py-2 transition-all flex items-center justify-between ${cls}`}>
                  {o.name}
                  {picked && o.code === q.code && <Check className="w-4 h-4" />}
                  {picked && o.code === picked && o.code !== q.code && <X className="w-4 h-4" />}
                </button>
              );
            })}
          </div>
          {picked && (
            <button type="button" onClick={next} data-testid="avdc-next-btn" className="mt-3 text-xs font-semibold text-white rounded-md px-3 py-1.5" style={{ background: ACCENT }}>
              {i + 1 >= order.length ? "See score" : "Next →"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/* ── Home-care advice beat ──────────────────────────────────────────────────── */
const HOMECARE = [
  { t: "Daily tooth brushing with pet-safe enzymatic paste", ok: true },
  { t: "VOHC-approved dental diet or dental chews", ok: true },
  { t: "Recheck and continue prescribed pain relief/antibiotics as directed", ok: true },
  { t: "Use human toothpaste (fluoride)", ok: false },
  { t: "Give hard bones, antlers or hooves to 'clean' teeth", ok: false },
  { t: "Skip brushing — wait until the next dental under GA", ok: false },
];

export function HomeCareCard() {
  const [sel, setSel] = useState(new Set());
  const [checked, setChecked] = useState(false);
  const toggle = (i) => { if (checked) return; setSel((p) => { const n = new Set(p); n.has(i) ? n.delete(i) : n.add(i); return n; }); };
  const correct = HOMECARE.reduce((a, h, i) => a + (h.ok === sel.has(i) ? 1 : 0), 0);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="homecare-card">
      <h3 className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-3">
        <Home className="w-3.5 h-3.5" /> Home-care advice for the owner
      </h3>
      <p className="text-[12px] text-slate-500 mb-2">Tap the advice you'd give after the dental procedure.</p>
      <div className="space-y-2">
        {HOMECARE.map((h, i) => {
          const on = sel.has(i);
          let cls = "border-slate-200 hover:border-slate-300 text-slate-700";
          if (checked) {
            if (h.ok) cls = "border-emerald-400 bg-emerald-50 text-emerald-700";
            else if (on) cls = "border-rose-400 bg-rose-50 text-rose-600 line-through";
            else cls = "border-slate-200 text-slate-400 opacity-70";
          } else if (on) cls = "border-[#0d9488] bg-[#0d9488]/5 text-[#0f766e]";
          return (
            <button key={i} type="button" disabled={checked} onClick={() => toggle(i)} data-testid={`homecare-${i}`} className={`w-full text-left text-[13px] font-medium border-2 rounded-md px-3 py-2 transition-all ${cls}`}>
              {checked && h.ok && "✓ "}{h.t}
            </button>
          );
        })}
      </div>
      {!checked ? (
        <button type="button" onClick={() => setChecked(true)} data-testid="homecare-check-btn" className="mt-3 text-xs font-semibold text-white rounded-md px-3 py-1.5" style={{ background: ACCENT }}>Check advice</button>
      ) : (
        <div className="mt-3 text-sm font-semibold" data-testid="homecare-result" style={{ color: correct === HOMECARE.length ? ACCENT : "#f59e0b" }}>
          {correct}/{HOMECARE.length} correct — brush daily, use VOHC products, and avoid hard chews and human paste.
        </div>
      )}
    </div>
  );
}
